import {
  addDays,
  endOfMonth,
  endOfWeek,
  format,
  isBefore,
  isToday,
  startOfMonth,
  startOfWeek,
} from "date-fns";
import { z } from "zod";

export interface DatesGetterOptions {
  locale: string;
}

export type CalendarDate = {
  id: string;
  displayDate: string;
  date: Date;
  isCurrentMonth: boolean;
  isToday: boolean;
};

export interface DaysGetter {
  getDate(): Date;
  getStartDay(): Date;
  getEndDay(): Date;
  getDates(): CalendarDate[];
}

type DateRange = {
  currentDate: Date;
  start: Date;
  end: Date;
};

export function getDaysInRange(dateRange: DateRange): CalendarDate[] {
  const month = dateRange.currentDate.getMonth();

  let startDay = dateRange.start;
  const endDay = dateRange.end;

  const dates: CalendarDate[] = [];

  while (isBefore(startDay, endDay)) {
    dates.push({
      id: format(startDay, "yyyy-MM-dd"),
      displayDate: format(startDay, "d"),
      date: startDay,
      isCurrentMonth: startDay.getMonth() === month,
      isToday: isToday(startDay),
    });
    startDay = addDays(startDay, 1);
  }

  return dates;
}

export class MonthDatesGetter implements DaysGetter {
  date: Date;
  startDay: Date;
  endDay: Date;

  constructor(date: Date, _options: DatesGetterOptions) {
    this.date = date;
    this.startDay = startOfWeek(startOfMonth(this.date), { weekStartsOn: 1 });
    this.endDay = endOfWeek(endOfMonth(this.date), { weekStartsOn: 1 });
  }

  getDate(): Date {
    return this.date;
  }

  getStartDay() {
    return this.startDay;
  }

  getEndDay() {
    return this.endDay;
  }

  getDates(): CalendarDate[] {
    return getDaysInRange({
      currentDate: this.date,
      start: this.startDay,
      end: this.endDay,
    });
  }
}

export function dateToDateUrl(date: Date) {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
}

export function dateFromDateUrl(date: string) {
  const [year, month, day] = date.split("-").map((value) => {
    return parseInt(value, 10);
  });

  return new Date(year, month - 1, day);
}

export const dateSearchParamSchema = z.iso.date().optional().catch(undefined);

export function getDateSearchParam(href: string): string | undefined {
  return dateSearchParamSchema.parse(
    new URL(href, "http://localhost").searchParams.get("date") ?? undefined,
  );
}
